import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth is via httpOnly cookie (set by the backend on login). withCredentials sends it automatically.
const api = axios.create({ baseURL: API, withCredentials: true });

// Multi-tenancy: attach the active registration so the backend scopes every
// query to the right registered entity. Falls back to the user's default when absent.
// NOTE: localStorage here holds ONLY the non-sensitive active-registration ID (a UI
// preference for the entity switcher). No credentials/tokens are ever stored client-side —
// the JWT rides a backend httpOnly cookie. (Static-analysis "insecure storage" flag is a false positive.)
export const REG_STORAGE_KEY = "claro_active_registration";
export const getActiveRegistration = () => localStorage.getItem(REG_STORAGE_KEY) || "";
export const setActiveRegistration = (id) => {
  if (id) localStorage.setItem(REG_STORAGE_KEY, id);
  else localStorage.removeItem(REG_STORAGE_KEY);
};
api.interceptors.request.use((config) => {
  const rid = localStorage.getItem(REG_STORAGE_KEY);
  if (rid) config.headers["X-Registration-Id"] = rid;
  // Signal "Entity View" (super-admin viewing as a customer) so the backend enforces read-only.
  if (localStorage.getItem("claro-view-as-customer") === "1") config.headers["X-Entity-View"] = "1";
  return config;
});

// Global session + resilience handler:
// - 429 (rate-limited by the ingress under a burst of requests): transparently retry with
//   backoff (honoring Retry-After) so transient throttling self-heals instead of crashing the UI.
// - 401 (expired/invalid session): clear the stale session and send the user to login.
let redirectingToLogin = false;
const MAX_429_RETRIES = 3;
api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const status = error?.response?.status;
    const config = error?.config;
    const url = config?.url || "";
    const isAuthProbe = url.includes("/auth/me") || url.includes("/auth/login");

    // Retry rate-limited requests with exponential backoff
    if (status === 429 && config && !config.__noRetry) {
      config.__retryCount = (config.__retryCount || 0) + 1;
      if (config.__retryCount <= MAX_429_RETRIES) {
        const retryAfter = parseFloat(error.response?.headers?.["retry-after"]);
        const delay = Number.isFinite(retryAfter)
          ? retryAfter * 1000
          : Math.min(600 * 2 ** (config.__retryCount - 1), 4000);
        await new Promise((r) => setTimeout(r, delay));
        return api(config);
      }
    }

    if (status === 401 && !isAuthProbe && !redirectingToLogin &&
        !window.location.pathname.startsWith("/login")) {
      redirectingToLogin = true;
      window.location.assign("/login");
    }
    // SaaS paywall: a 402 (out of AI credits, feature not in plan, trial/locked)
    // → surface a global upgrade modal. detail may arrive as a Blob on downloads.
    if (status === 402) {
      // Background polls (e.g. feature availability checks) opt out of the global
      // upgrade modal via config.__silent402 — a paywall banner should only appear
      // from a user action, not a routine poll on a plan that lacks that feature.
      if (!config?.__silent402) {
        let detail = error?.response?.data?.detail;
        if (detail == null && error?.response?.data && typeof error.response.data.text === "function") {
          try { detail = JSON.parse(await error.response.data.text()).detail; } catch (e) { /* keep null */ }
        }
        window.dispatchEvent(new CustomEvent("claro:upgrade-required", { detail: detail || {} }));
      }
    }

    // Grace period lapsed mid-session → backend forces re-login + MFA enrollment.
    if (status === 403 && error?.response?.data?.detail === "mfa_enrollment_required" &&
        !redirectingToLogin && !window.location.pathname.startsWith("/login")) {
      redirectingToLogin = true;
      window.location.assign("/login");
    }
    // Super-admin "Entity View" is read-only → notify the UI to toast (no state change made).
    if (status === 403 && error?.response?.data?.detail?.read_only) {
      window.dispatchEvent(new CustomEvent("claro:entity-view-readonly", { detail: error.response.data.detail }));
    }
    return Promise.reject(error);
  }
);

// Run a long AI generation as a background job: POST returns {job_id}; poll
// the job until done so the HTTP request never stays open (avoids the 100s
// reverse-proxy timeout). Falls back to returning data directly if an endpoint
// is not job-based. Throws an axios-shaped error on failure so existing
// `formatApiError(e.response?.data?.detail)` catch blocks keep working.
export async function runAiJob(path, body, config, opts = {}) {
  const { maxTries = 150 } = opts;
  const { data } = await api.post(path, body, config);
  if (!data || !data.job_id) return data;
  const jobId = data.job_id;
  // Poll (default ~5 min = 150 * 2s; callers with long jobs can raise maxTries).
  for (let i = 0; i < maxTries; i++) {
    await new Promise((r) => setTimeout(r, 2000));
    const { data: job } = await api.get(`/ai/jobs/${jobId}`);
    if (job.status === "done") return job.result;
    if (job.status === "error") {
      const err = new Error(typeof job.error === "string" ? job.error : "AI generation failed.");
      err.response = { data: { detail: job.error }, status: job.status_code || 502 };
      throw err;
    }
  }
  const timeout = new Error("AI generation timed out. Please try again.");
  timeout.response = { data: { detail: "AI generation timed out. Please try again." } };
  throw timeout;
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  // structured errors (e.g. feature_locked) carry a human message
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.detail === "string") return detail.detail;
  return "Something went wrong. Please try again.";
}

// Download a tenant-scoped file via axios (so the X-Registration-Id header + auth
// cookie are sent — a raw window.open navigation would drop the header and hit the
// wrong entity). Reads the server filename from Content-Disposition when present.
export async function downloadBlob(path, fallbackName) {
  let resp;
  // Cache-bust every export: the download URLs are stable and end in .xlsx/.pdf/.docx,
  // which CDNs (Cloudflare) cache by URL by default — so without a unique query param a
  // stale file is served even after a redeploy. A fresh param = a URL the edge never cached.
  const bust = `_cb=${Date.now()}`;
  const reqUrl = path + (path.includes("?") ? "&" : "?") + bust;
  try {
    resp = await api.get(reqUrl, { responseType: "blob", headers: { "Cache-Control": "no-cache" } });
  } catch (err) {
    // error body arrives as a Blob when responseType is blob — decode it for a useful message
    let detail = "Download failed. Please try again.";
    try { detail = formatApiError(JSON.parse(await err.response.data.text()).detail); } catch (e) { /* keep default */ }
    const e = new Error(detail);
    e.response = { data: { detail } };
    throw e;
  }
  let name = fallbackName || "download";
  const cd = resp.headers["content-disposition"] || "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  if (m) name = m[1];
  const url = URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default api;
